/**
 * Assessment editor drawer
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Play, Save as SaveIcon, X, Loader2 } from "lucide-react";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "../../components/layout";
import { Chip } from "../../components/ui";
import { styles, btnFilled, btnPlain, spin, statusTone } from "./styles";
import type { UploadedAssessment, BusyState, InstrumentDefinition } from "./types";

type Props = {
  item: UploadedAssessment;
  busy: BusyState[string] | undefined;
  editParserText: string;
  setEditParserText: (text: string) => void;
  resultText: string;
  onRun: () => void;
  onSave: () => void;
  onClose: () => void;
  instrument?: InstrumentDefinition;
  uiLang: "en" | "hu";
};

const fmtDate = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const AssessmentDrawer: React.FC<Props> = ({
  item,
  busy,
  editParserText,
  setEditParserText,
  resultText,
  onRun,
  onSave,
  onClose,
  instrument,
  uiLang,
}) => {
  const { t } = useTranslation();
  const isQuestionnaire = item.kind === "questionnaire";
  const isPending = item.parseStatus === "pending" || busy === "parse";
  const disabled = !!busy || item.parseStatus === "pending";

  // Custom (file-based) sends carry their own snapshot (name/items/scale,
  // localized to a single lang at send time); built-ins resolve through the
  // shared `instrument` prop (all langs, severity bands).
  const isCustomSend = !!item.fileId;
  const snapshot = item.instrumentSnapshot;
  const isManual = !!item.completedManually;
  const band = isQuestionnaire ? instrument?.scoring.bands.find((b) => b.severity === item.severity) : undefined;
  const isCompleted = item.status === "completed";
  const qTone = band?.tone ?? item.severityTone;
  const qChipTone = !isCompleted
    ? "muted"
    : isManual
    ? "ok"
    : qTone === "caution"
    ? "warn"
    : qTone === "concern"
    ? "bad"
    : "ok";
  // Custom (raw-sum) sends have no severity band — a bare score, not
  // "undefined", when there's no band/severity to show alongside it.
  const bandLabel = band?.label[uiLang] ?? item.severity ?? null;
  const scoreLabel = bandLabel ? `${item.score} · ${bandLabel}` : typeof item.score === "number" ? `${item.score}` : null;
  const displayName = item.instrumentSnapshotName ?? instrument?.name[uiLang] ?? item.instrumentId;
  const headerTitle = isQuestionnaire ? displayName : item.filename;
  const channelChipText =
    item.channel === "email" ? t("assessments.channelEmail") : item.channel === "app" ? t("assessments.channelApp") : null;

  const answerRows: { key: string; text: string; answer: string }[] = React.useMemo(() => {
    if (!isQuestionnaire || !isCompleted || isManual || !item.responses) return [];
    if (isCustomSend) {
      if (!snapshot) return [];
      return snapshot.items.map((q) => {
        const v = item.responses?.[String(q.id)];
        const step = snapshot.scale.find((s) => s.value === v);
        return { key: q.id, text: q.text, answer: step ? `${step.label} (${v})` : `— (${v ?? "—"})` };
      });
    }
    if (!instrument) return [];
    return instrument.items.map((q) => {
      const v = item.responses?.[String(q.id)];
      const step = instrument.scale.find((s) => s.value === v);
      const lang = item.lang ?? uiLang;
      return {
        key: String(q.id),
        text: q.text[lang],
        answer: step ? `${step.label[lang]} (${v})` : `— (${v ?? "—"})`,
      };
    });
  }, [isQuestionnaire, isCompleted, isManual, isCustomSend, snapshot, instrument, item.responses, item.lang, uiLang]);

  const parseStatusLabel = t(
    item.parseStatus === "ok"
      ? "assessments.statusParsed"
      : item.parseStatus === "pending"
      ? "assessments.statusParsing"
      : item.parseStatus === "error"
      ? "assessments.statusError"
      : "assessments.statusNotParsed"
  );

  return (
    <Drawer open={true} onClose={onClose}>
      <div style={styles.drawerHeader}>
        <div style={{ display: "grid", gap: 2 }}>
          <div style={{ fontWeight: 900, fontSize: 16, color: "var(--color-title)" }}>{headerTitle}</div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {isQuestionnaire ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {channelChipText ? <Chip text={channelChipText} tone="muted" /> : null}
                <Chip
                  text={
                    !isCompleted
                      ? t("assessments.statusSent")
                      : isManual
                      ? t("assessments.statusCompletedManually")
                      : scoreLabel ?? t("assessments.completedAt")
                  }
                  tone={qChipTone}
                />
              </span>
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {isPending ? <Loader2 size={14} style={spin} /> : null}
                <Chip text={parseStatusLabel} tone={statusTone(item.parseStatus)} />
              </span>
            )}

            {isQuestionnaire ? (
              <>
                <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                  {t("assessments.sentAt")}: {item.sentAt ? fmtDate(item.sentAt) : "—"}
                </span>
                {item.completedAt ? (
                  <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                    {t("assessments.completedAt")}: {fmtDate(item.completedAt)}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                  {t("assessments.uploadedAt")}: {fmtDate(item.createdAt)}
                </span>
                {item.parseModel ? (
                  <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                    {t("assessments.parseModel")}: {item.parseModel}
                  </span>
                ) : null}
                {item.parsedAt ? (
                  <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                    {t("assessments.parsedAt")}: {fmtDate(item.parsedAt)}
                  </span>
                ) : null}
                {item.tpPk && item.sk ? (
                  <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                    {t("assessments.keysLabel")}: {item.tpPk} / {item.sk}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: "var(--color-danger)", fontWeight: 800 }}>
                    {t("assessments.keysMissing")}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <button
          style={{
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            borderRadius: 10,
            width: 32,
            height: 32,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={onClose}
          title={t("common.close")}
          aria-label={t("common.close")}
        >
          <X size={16} />
        </button>
      </div>

      <div style={{ display: "grid", gap: 12, overflow: "auto" }}>
        {isQuestionnaire ? (
          !isCustomSend && !instrument ? (
            <div style={{ color: "var(--color-danger)" }}>{t("assessments.definitionsUnavailable")}</div>
          ) : (
            <div>
              <div style={{ marginBottom: 12 }}>
                <strong>{displayName}</strong>
                {isManual ? (
                  <div>{t("assessments.completedManuallyNote")}</div>
                ) : isCompleted ? (
                  <div>
                    {t("assessments.score")}
                    {scoreLabel ? `: ${scoreLabel}` : ""}
                  </div>
                ) : (
                  <div>{t("assessments.awaitingResponse")}</div>
                )}
              </div>
              {isCompleted && !isManual && answerRows.length > 0 && (
                <ol style={{ paddingLeft: 20, display: "flex", flexDirection: "column", gap: 8 }}>
                  {answerRows.map((row) => (
                    <li key={row.key}>
                      <div>{row.text}</div>
                      <div style={{ opacity: 0.75, fontSize: 13 }}>{row.answer}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )
        ) : (
          <>
            <div style={styles.row}>
              <div style={styles.label}>{t("assessments.parserPrompt")}</div>
              <textarea
                value={editParserText}
                onChange={(e) => setEditParserText(e.target.value)}
                style={styles.textarea}
                placeholder={t("assessments.parserPromptPlaceholder")}
                disabled={disabled}
              />
            </div>

            <div style={styles.row}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={styles.label}>{t("assessments.result")}</div>
                {item.parseStatus === "error" && item.parseError ? (
                  <span style={{ fontSize: 12, color: "var(--color-danger)", fontWeight: 800 }}>{item.parseError}</span>
                ) : null}
              </div>

              <div style={styles.resultBox}>
                {resultText?.trim() ? (
                  resultText
                ) : isPending ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Loader2 size={14} style={spin} /> {t("assessments.parsing")}
                  </span>
                ) : (
                  t("assessments.noResultYet")
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {isQuestionnaire ? null : (
          <>
            <button
              style={btnFilled("var(--color-brand)", "#fff")}
              onClick={onRun}
              disabled={disabled}
              title={t("common.run")}
            >
              {isPending ? <Loader2 size={16} style={spin} /> : <Play size={16} />}
              {t("common.run")}
            </button>

            <button
              style={btnFilled("var(--color-success)", "#fff")}
              onClick={onSave}
              disabled={disabled}
              title={t("common.save")}
            >
              <SaveIcon size={16} />
              {t("common.save")}
            </button>
          </>
        )}

        <button style={btnPlain} onClick={onClose} title={t("common.close")}>
          <X size={16} />
          {t("common.close")}
        </button>
      </div>
    </Drawer>
  );
};
